import React, { useState, useEffect } from 'react';
import { KnowledgeCategoryService, KnowledgeCategory, CreateCategoryData } from '../../services/knowledgeCategoryService';
import './CategoryForm.css';

interface CategoryFormProps {
  category?: KnowledgeCategory | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULT_PROMPT_TEMPLATE = `You are an expert educator creating comprehensive learning lessons.

Your task is to generate an educational lesson on the requested topic. The lesson should be in Vietnamese.

REQUIREMENTS:
- Create detailed, well-structured content in markdown format
- Include clear explanations with examples
- Use appropriate headers, lists, and formatting. Don't use tables.
- Make the content engaging and easy to understand
- Aim for approximately 800-1200 words
- Include practical examples and applications where relevant

IMPORTANT FOR CODE BLOCKS:
- If you need to show code examples, use single backticks for inline code
- For code blocks, properly escape them in the JSON by using \\n for newlines
- Example: "\\n\\n\`\`\`python\\nprint('Hello')\\n\`\`\`\\n\\n"

The lesson should be:
- Clear and well-organized
- Accessible to learners at appropriate level
- Rich with examples and explanations
- Formatted professionally in markdown`;

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onClose,
  onSuccess
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [promptTemplate, setPromptTemplate] = useState(DEFAULT_PROMPT_TEMPLATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'edit' | 'image'>('edit');
  
  // Image generation states
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<{ data: string; mimeType: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageSuccess, setImageSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (category) {
      setCategoryName(category.category_name);
      setDescription(category.description || '');
      setPromptTemplate(category.prompt_template);
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (!promptTemplate.trim()) {
      setError('Prompt template is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const data: CreateCategoryData = {
        category_name: categoryName.trim(),
        description: description.trim() || undefined,
        prompt_template: promptTemplate.trim(),
        lesson_structure: {
          has_vocabulary: false,
          custom_fields: []
        }
      };

      if (category) {
        await KnowledgeCategoryService.updateCategory(category.category_id, data);
      } else {
        await KnowledgeCategoryService.createCategory(data);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleGenerateImage = async () => {
    if (!category || !imagePrompt.trim()) {
      setError('Please enter an image prompt');
      return;
    }

    try {
      setGeneratingImage(true);
      setError(null);
      setImageSuccess(null);

      const result = await KnowledgeCategoryService.generateCategoryImage(
        category.category_id,
        imagePrompt.trim()
      );

      setGeneratedImage({
        data: result.image,
        mimeType: result.mimeType
      });
      setImageSuccess('Image generated successfully! Click "Save Image" to upload.');
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSaveImage = async () => {
    if (!category || !generatedImage) return;

    try {
      setUploadingImage(true);
      setError(null);
      setImageSuccess(null);

      await KnowledgeCategoryService.uploadCategoryImage(
        category.category_id,
        generatedImage.data,
        generatedImage.mimeType,
        imagePrompt.trim()
      );

      setImageSuccess('Image saved successfully!');
      setGeneratedImage(null);
      setImagePrompt('');
      
      // Trigger success callback to refresh category data
      onSuccess();
    } catch (err) {
      console.error('Error saving image:', err);
      setError(err instanceof Error ? err.message : 'Failed to save image');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <>
      <div className="category-form-overlay" onClick={handleOverlayClick} />
      <div className="category-form-modal">
        <div className="category-form-header">
          <h2 className="category-form-title">
            {category ? 'Edit Category' : 'Create Category'}
          </h2>
          <button 
            className="category-form-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Tabs - Only show when editing an existing category */}
        {category && (
          <div className="category-form-tabs">
            <button
              type="button"
              className={`category-form-tab ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => setActiveTab('edit')}
            >
              Edit Category
            </button>
            <button
              type="button"
              className={`category-form-tab ${activeTab === 'image' ? 'active' : ''}`}
              onClick={() => setActiveTab('image')}
            >
              Generate Image
            </button>
          </div>
        )}

        {/* Edit Tab Content */}
        {activeTab === 'edit' && (
          <>
            <form onSubmit={handleSubmit} className="category-form-content">
              {error && (
                <div className="category-form-error">
                  {error}
                </div>
              )}

              <div className="category-form-field">
                <label htmlFor="category-name" className="category-form-label">
                  Category Name <span className="category-form-required">*</span>
                </label>
                <input
                  id="category-name"
                  type="text"
                  className="category-form-input"
                  placeholder="e.g., Japanese, Physics, Cooking"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="category-form-field">
                <label htmlFor="category-description" className="category-form-label">
                  Description (Optional)
                </label>
                <input
                  id="category-description"
                  type="text"
                  className="category-form-input"
                  placeholder="Brief description of this category"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="category-form-field">
                <label htmlFor="prompt-template" className="category-form-label">
                  AI Prompt Template <span className="category-form-required">*</span>
                </label>
                <textarea
                  id="prompt-template"
                  className="category-form-textarea"
                  placeholder="Enter the AI prompt template for generating lessons..."
                  value={promptTemplate}
                  onChange={(e) => setPromptTemplate(e.target.value)}
                  disabled={submitting}
                  rows={15}
                />
                <div className="category-form-hint">
                  This template guides the AI in generating lessons for this category.
                  The AI will use this prompt along with past lesson context.
                </div>
              </div>
            </form>

            <div className="category-form-footer">
              <button 
                type="button"
                className="category-form-btn category-form-btn-cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="category-form-btn category-form-btn-submit"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (category ? 'Update' : 'Create')}
              </button>
            </div>
          </>
        )}

        {/* Image Generation Tab Content - Only show when editing an existing category */}
        {activeTab === 'image' && category && (
          <>
            <div className="category-form-content">
              {error && (
                <div className="category-form-error">
                  {error}
                </div>
              )}

              {imageSuccess && (
                <div className="category-form-success">
                  {imageSuccess}
                </div>
              )}

              <div className="category-form-field">
                <label htmlFor="image-prompt" className="category-form-label">
                  Image Prompt
                </label>
                <textarea
                  id="image-prompt"
                  className="category-form-textarea"
                  placeholder="Describe the image you want to generate for this category..."
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  disabled={generatingImage || uploadingImage}
                  rows={4}
                />
              </div>

              <button
                type="button"
                className="category-form-btn category-form-btn-generate"
                onClick={handleGenerateImage}
                disabled={generatingImage || uploadingImage || !imagePrompt.trim()}
              >
                {generatingImage ? 'Generating...' : 'Generate Image'}
              </button>

              {generatedImage && (
                <div className="category-form-image-preview">
                  <img
                    src={`data:${generatedImage.mimeType};base64,${generatedImage.data}`}
                    alt="Generated category"
                    className="category-form-preview-image"
                  />
                  <button
                    type="button"
                    className="category-form-btn category-form-btn-submit"
                    onClick={handleSaveImage}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Saving...' : 'Save Image'}
                  </button>
                </div>
              )}

              {category.image_url && (
                <div className="category-form-current-image">
                  <label className="category-form-label">Current Category Image</label>
                  <img
                    src={category.image_url}
                    alt={category.category_name}
                    className="category-form-preview-image"
                  />
                </div>
              )}
            </div>

            <div className="category-form-footer">
              <button 
                type="button"
                className="category-form-btn category-form-btn-cancel"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CategoryForm;

